import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, BookOpen, Users, TrendingUp, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function Substack() {
  const [animationMode, setAnimationMode] = useState<'ink' | 'neural'>('neural');
  // Load Substack embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://substack.com/embedjs/embed.js';
    script.async = true;
    script.charset = 'utf-8';
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  // Ink diffusion blobs
  const inkBlobs = Array.from({
    length: 8
  }, (_, i) => ({
    id: i,
    x: 20 + Math.random() * 60,
    y: 20 + Math.random() * 60,
    size: 30 + Math.random() * 40,
    duration: 15 + Math.random() * 10,
    delay: Math.random() * -20
  }));
  // Neural network nodes
  const nodes = Array.from({
    length: 20
  }, (_, i) => ({
    id: i,
    x: 10 + Math.random() * 80,
    y: 10 + Math.random() * 80,
    size: 0.3 + Math.random() * 0.5,
    pulseDelay: Math.random() * 5,
    pulseDuration: 2 + Math.random() * 2
  }));
  // Generate connections between nearby nodes
  const connections = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[j].x - nodes[i].x;
      const dy = nodes[j].y - nodes[i].y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < 25 && Math.random() > 0.5) {
        connections.push({
          id: `${i}-${j}`,
          x1: nodes[i].x,
          y1: nodes[i].y,
          x2: nodes[j].x,
          y2: nodes[j].y,
          delay: Math.random() * 8,
          duration: 3 + Math.random() * 3
        });
      }
    }
  }
  const whySubscribeCards = [{
    icon: BookOpen,
    title: 'Original Research',
    description: 'Deep dives into nature-inspired innovation',
    color: '#78e1c4'
  }, {
    icon: Users,
    title: 'Founder Spotlights',
    description: 'Conversations with the builders we back',
    color: '#4DE7D4'
  }, {
    icon: TrendingUp,
    title: 'Investment Insights',
    description: 'How regenerative systems compound value',
    color: '#a0e6e9'
  }];
  return <div className="min-h-screen pt-20 bg-deepCanopy" data-id="element-1538">
      {/* Hero Section with Neural Network Animation */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-b from-[#0e2533] via-[#1c3f4d] to-deepCanopy" data-id="element-1539">
        {/* Neural Network Background - Full Width */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40" data-id="element-1540">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-1541">
            <defs data-id="element-1542">
              <filter id="node-glow" data-id="element-1543">
                <feGaussianBlur stdDeviation="0.8" result="coloredBlur" data-id="element-1544" />
                <feMerge data-id="element-1545">
                  <feMergeNode in="coloredBlur" data-id="element-1546" />
                  <feMergeNode in="SourceGraphic" data-id="element-1547" />
                </feMerge>
              </filter>
              <linearGradient id="connection-gradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-1548">
                <stop offset="0%" stopColor="#78e1c4" stopOpacity="0" data-id="element-1549" />
                <stop offset="50%" stopColor="#4DE7D4" stopOpacity="1" data-id="element-1550" />
                <stop offset="100%" stopColor="#78e1c4" stopOpacity="0" data-id="element-1551" />
              </linearGradient>
            </defs>

            {connections.map(conn => <motion.line key={conn.id} x1={conn.x1} y1={conn.y1} x2={conn.x2} y2={conn.y2} stroke="url(#connection-gradient)" strokeWidth="0.15" strokeLinecap="round" initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: [0, 1, 0],
            opacity: [0, 0.8, 0]
          }} transition={{
            duration: conn.duration,
            repeat: Infinity,
            delay: conn.delay,
            ease: 'easeInOut'
          }} data-id="element-1552" />)}

            {nodes.map(node => <g key={node.id} data-id="element-1553">
                <motion.circle cx={node.x} cy={node.y} r={node.size} fill="none" stroke="#78e1c4" strokeWidth="0.1" initial={{
              r: node.size,
              opacity: 0
            }} animate={{
              r: [node.size, node.size * 3, node.size],
              opacity: [0, 0.6, 0]
            }} transition={{
              duration: node.pulseDuration,
              repeat: Infinity,
              delay: node.pulseDelay,
              ease: 'easeOut'
            }} data-id="element-1554" />

                <motion.circle cx={node.x} cy={node.y} r={node.size * 0.6} fill="#4DE7D4" filter="url(#node-glow)" initial={{
              opacity: 0.3
            }} animate={{
              opacity: [0.3, 1, 0.3],
              scale: [1, 1.2, 1]
            }} transition={{
              duration: node.pulseDuration,
              repeat: Infinity,
              delay: node.pulseDelay,
              ease: 'easeInOut'
            }} data-id="element-1555" />
              </g>)}
          </svg>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-1556">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight" data-id="element-1557">
            Vitality Substack
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-1558">
            Our deeper thinking on biomimicry, deep tech, and building what
            lasts.
          </motion.p>
        </div>
      </section>

      {/* Embedded Substack Feed Section */}
      <section className="py-16 bg-deepCanopy" data-id="element-1559">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1560">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="relative" data-id="element-1561">
            {/* Bio-fusion styled frame with teal glow */}
            <div className="absolute -inset-4 bg-gradient-to-r from-vitalityTeal/20 via-bioLuminescentTeal/10 to-vitalityTeal/20 rounded-3xl blur-xl" data-id="element-1562" />

            <div className="relative bg-[#1c3f4d]/40 backdrop-blur-sm border-2 border-vitalityTeal/30 rounded-2xl overflow-hidden shadow-2xl" data-id="element-1563">
              {/* Organic corner accents */}
              <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-vitalityTeal/50 rounded-tl-2xl" data-id="element-1564" />
              <div className="absolute top-0 right-0 w-20 h-20 border-t-2 border-r-2 border-vitalityTeal/50 rounded-tr-2xl" data-id="element-1565" />
              <div className="absolute bottom-0 left-0 w-20 h-20 border-b-2 border-l-2 border-vitalityTeal/50 rounded-bl-2xl" data-id="element-1566" />
              <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-vitalityTeal/50 rounded-br-2xl" data-id="element-1567" />

              <div className="p-4 sm:p-8 flex justify-center" data-id="element-1568">
                <div className="substack-post-embed w-full max-w-2xl" dangerouslySetInnerHTML={{
                __html: '<p lang="en">Scaling Without Shrinking the World by Vitality VF</p><p>AI, Efficiency, and the Future of Resilient Growth</p><a data-post-link href="https://vitalityvf.substack.com/p/scaling-without-shrinking-the-world">Read on Substack</a>'
              }} data-id="element-1569" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Subscribe Section */}
      <section className="py-20 bg-gradient-to-b from-deepCanopy to-[#0e2533]" data-id="element-1570">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1571">
          {/* CTA Button */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-1572">
            <a href="https://substack.com/@vitalityvf" target="_blank" rel="noopener noreferrer" data-id="element-1573">
              <Button variant="primary" className="group" data-id="element-1574">
                Subscribe Now
                <ExternalLink className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" data-id="element-1575" />
              </Button>
            </a>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-16" data-id="element-1576">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1577">
              Why Subscribe
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-1578">
              Our Substack is where we go deeper — exploring the patterns nature
              has perfected over billions of years and how they're shaping the
              next generation of essential technologies. Subscribe for original
              research, founder spotlights, and investment insights rooted in
              biomimicry.
            </p>
          </motion.div>

          {/* Three icon cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-1579">
            {whySubscribeCards.map((card, index) => <motion.div key={card.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.15
          }} className="relative group" data-id="element-1580">
                {/* Glow effect */}
                <div className="absolute -inset-2 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
              backgroundColor: `${card.color}30`
            }} data-id="element-1581" />

                <div className="relative bg-[#1c3f4d]/60 backdrop-blur-sm border-2 rounded-2xl p-8 h-full transition-all duration-300 group-hover:border-opacity-100" style={{
              borderColor: `${card.color}40`
            }} data-id="element-1582">
                  {/* Icon container */}
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto" style={{
                backgroundColor: `${card.color}20`,
                border: `2px solid ${card.color}60`
              }} data-id="element-1583">
                    <card.icon className="w-8 h-8" style={{
                  color: card.color
                }} data-id="element-1584" />
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-3 text-center" data-id="element-1585">
                    {card.title}
                  </h3>
                  <p className="text-gray-300 text-center leading-relaxed" data-id="element-1586">
                    {card.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
    </div>;
}